import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';

import HelpButton from 'help/HelpButton';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {GRAY_DARKER, RED_DARK} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';

import FieldLabel from './FieldLabel';
import {INPUT, InputErrorMessageStyle, InputProps, Size} from './InputConstants';

export interface InputWrapperProps extends InputProps {
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  id: string;
  helpInteraction?: FloatingInteraction.CLICK | FloatingInteraction.HOVER;
}

const styles = StyleSheet.create({
  errorMessage: {
    ...FONT_PROXIMA_11,
  },
  errorMessageInfo: {
    color: GRAY_DARKER,
  },
  errorMessageError: {
    color: RED_DARK,
  },
});

const ERROR_MESSAGE_STYLE: Record<InputErrorMessageStyle, StyleDeclaration> = {
  [InputErrorMessageStyle.INFO]: styles.errorMessageInfo,
  [InputErrorMessageStyle.ERROR]: styles.errorMessageError,
};

/**
 * Component for input of string fields, providing labels, icons, and validation.
 * Label styles are re-exported on this class.
 */
// eslint-disable-next-line react/prefer-stateless-function
export default class InputWrapper extends React.Component<InputWrapperProps> {
  static propTypes = {
    ...INPUT,
    children: PropTypes.node.isRequired,
    id: PropTypes.string.isRequired,
  };

  static defaultProps = {
    isRequired: false,
    labelStyle: FieldLabel.STYLES.HIDDEN,
    size: Size.SMALL,
    helpInteraction: FloatingInteraction.CLICK,
  };

  override render(): JSX.Element {
    return (
      <div className={this.props.className} {...getTestProps(this.props.dataTestAttrs)}>
        <div
          className={classNames('fieldInput-label-container', {
            'sr-only': this.props.labelStyle === FieldLabel.STYLES.HIDDEN,
          })}
        >
          <FieldLabel
            htmlFor={this.props.id}
            isRequired={this.props.isRequired}
            label={this.props.label}
            labelStyle={this.props.labelStyle}
          />
          {this.props.help && (
            <HelpButton
              renderContent={() => this.props.help}
              interaction={this.props.helpInteraction}
              iconClassName="u-mini-spacing-left"
            />
          )}
        </div>
        {this.props.description && (
          <div className={classNames('fieldInput-description', this.props.descriptionClassName)}>
            {this.props.description}
          </div>
        )}
        <div className="u-flex-display">
          {this.props.children}
          {this.props.button && React.cloneElement(this.props.button, {className: 'button--inline'})}
        </div>
        {this.props.error && (
          <div
            className={classNames(css(styles.errorMessage), css(ERROR_MESSAGE_STYLE[this.props.error.style]))}
          >
            {this.props.error.message}
          </div>
        )}
      </div>
    );
  }
}
